import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { coursesAPI, examsAPI, uploadsAPI, analyticsAPI } from "../services/api";
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import OnboardingTour from '../components/OnboardingTour';
import ActivationChecklist from '../components/ActivationChecklist';
import "../styles/Dashboard.css";
import {
  FaBook,
  FaChalkboardTeacher,
  FaUsers,
  FaFileAlt,
  FaClipboardList,
  FaCog,
} from "react-icons/fa";

const Dashboard = () => {
  const [stats, setStats] = useState({
    courses: 0,
    students: 0,
    lectures: 0,
    exams: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const userName = localStorage.getItem("userName") || "المستكشف";
  const userRole = localStorage.getItem("userRole");

  useEffect(() => {
    const fetchDashboardData = async () => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      try {
        // 1. Get stats and activity in a single call
        const statsRes = await analyticsAPI.getStats();
        const { totals, recentEnrollments } = statsRes.data;

        setStats({
          courses: totals.totalCourses,
          students: totals.totalStudents,
          lectures: totals.totalLectures,
          exams: totals.totalExams
        });
        setRecentActivity(recentEnrollments);
        setLoading(false);

      } catch (error) {
        console.error("❌ خطأ أثناء تحميل بيانات لوحة التحكم:", error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("authToken");
          navigate("/login");
        }
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  return (
    <div className="dashboard-container">
      <OnboardingTour />
      {/* Show activation checklist only for teachers, hidden once fully activated */}
      {(userRole === 'teacher') && <ActivationChecklist />}
      <div className="welcome-banner">
        <h1>{userRole === 'owner' || userRole === 'admin' ? 'مرحباً بك، أيها المدير! 👑' : `مرحباً بك، يا ${userName}! 👋`}</h1>
        <p>{userRole === 'owner' || userRole === 'admin' ? 'إليك لقطة شاملة لأداء المنصة بالكامل.' : 'إليك نظرة سريعة على أداء دوراتك اليوم.'}</p>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>⏳ جارٍ تحميل البيانات الحقيقية...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="card stat-card primary">
              <div className="stat-icon"><FaBook /></div>
              <div className="stat-info">
                <h3>الدورات {userRole === 'owner' || userRole === 'admin' ? 'الكلية' : 'النشطة'}</h3>
                <p className="stat-value">{stats.courses}</p>
              </div>
            </div>

            <div className="card stat-card success">
              <div className="stat-icon"><FaUsers /></div>
              <div className="stat-info">
                <h3>{userRole === 'owner' || userRole === 'admin' ? 'إجمالي الطلاب' : 'طلابك المشتركون'}</h3>
                <p className="stat-value">{stats.students}</p>
              </div>
            </div>

            <div className="card stat-card warning">
              <div className="stat-icon"><FaFileAlt /></div>
              <div className="stat-info">
                <h3>الامتحانات المرفوعة</h3>
                <p className="stat-value">{stats.exams}</p>
              </div>
            </div>

            <div className="card stat-card info">
              <div className="stat-icon"><FaChalkboardTeacher /></div>
              <div className="stat-info">
                <h3>المحاضرات المعدة</h3>
                <p className="stat-value">{stats.lectures}</p>
              </div>
            </div>
          </div>

          {/* Analytics Section */}
          <AnalyticsDashboard />

          {/* Recent Activity & Quick Actions */}
          <div className="dashboard-sections">
            <div className="card recent-activity">
              <h3>🕒 النشاط الأخير</h3>
              {recentActivity.length > 0 ? (
                <ul className="activity-list">
                  {recentActivity.map((activity, index) => (
                    <li key={index} className="activity-item">
                      <span className="activity-icon">{activity.status === 'approved' ? '✅' : '⏳'}</span>
                      <div className="activity-details">
                        <p><strong>{activity.full_name}</strong> انضم إلى دورة <strong>{activity.course_title}</strong></p>
                        <span className="activity-time">{new Date(activity.enrolled_at).toLocaleDateString('ar-EG', { day: 'numeric', month: 'long' })}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-activity">
                  <p>لا يوجد نشاط انضمام حديث حتى الآن.</p>
                </div>
              )}
            </div>

            <div className="card quick-actions">
              <h3>⚡ إجراءات سريعة</h3>
              <div className="actions-grid">
                <button onClick={() => navigate('/add-course')} className="action-btn">
                  ➕ إضافة دورة
                </button>
                <button onClick={() => navigate('/upload')} className="action-btn">
                  📤 رفع محاضرة
                </button>
                <button onClick={() => navigate('/add-exam-questions')} className="action-btn">
                  📝 إنشاء امتحان
                </button>
                {(userRole === "owner" || userRole === "admin") && (
                  <button onClick={() => navigate('/students')} className="action-btn">
                    👨‍🎓 إدارة الطلاب
                  </button>
                )}

                {(userRole === "owner" || userRole === "admin") && (
                  <button onClick={() => navigate('/admin/teachers')} className="action-btn admin-btn" style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                    👥 إدارة المعلمين
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
